/**
 * Shared control primitives.
 *
 * Small, unstyled-by-default wrappers over Base UI so every panel gets the same
 * idioms without repeating Tailwind soup. The visual language is fixed here:
 * serif labels, hairline rules, 2px radii, no shadows.
 */

import type { ReactNode } from 'react';

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block" title={hint}>
      <span className="mb-0.5 block text-[11px] leading-tight text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

export function SliderRow({
  label,
  hint,
  value,
  min,
  max,
  step,
  unit,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="py-[3px]" title={hint}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] leading-tight text-ink-muted">{label}</span>
        <span className="font-mono text-[10px] tabular-nums text-ink-faint">
          {formatNumber(value)}
          {unit ? <span className="ml-0.5">{unit}</span> : null}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-1.5 py-[3px]" title={hint}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-[11px] leading-tight text-ink">{label}</span>
    </label>
  );
}

export function NumberInput({
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => {
        const v = Number(e.target.value);
        if (Number.isFinite(v)) onChange(v);
      }}
      className="w-full border border-rule bg-paper-raised px-1.5 py-[3px] font-mono text-[11px] tabular-nums text-ink outline-none focus:border-rule-strong"
    />
  );
}

export function Select<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <select
      value={String(value)}
      onChange={(e) => {
        const raw = e.target.value;
        const match = options.find((o) => String(o.value) === raw);
        if (match) onChange(match.value);
      }}
      className="w-full appearance-none border border-rule bg-paper-raised px-1.5 py-[3px] text-[11px] text-ink outline-none focus:border-rule-strong"
    >
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** A titled block of controls, matching the paper-panel look. */
export function Group({
  title,
  right,
  children,
}: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-b border-rule px-3 py-2.5 last:border-b-0">
      <header className="mb-1.5 flex items-center justify-between">
        <h2 className="text-[10px] font-normal uppercase tracking-[0.14em] text-ink-faint">{title}</h2>
        {right}
      </header>
      {children}
    </section>
  );
}

/** Compact labelled statistic for the Info panel. */
export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-[2px]" title={hint}>
      <span className="text-[11px] text-ink-muted">{label}</span>
      <span className="font-mono text-[11px] tabular-nums text-ink">{value}</span>
    </div>
  );
}

export function formatNumber(v: number): string {
  if (!Number.isFinite(v)) return '—';
  const a = Math.abs(v);
  if (a === 0) return '0';
  if (a >= 1e6 || a < 1e-3) return v.toExponential(1);
  if (a >= 1000) return v.toFixed(0);
  if (a >= 10) return v.toFixed(1);
  if (a >= 1) return v.toFixed(2);
  return v.toFixed(3);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
